import type { IDealSummary, FeatureToggles, IIncentives, PaymentGridBaggagePayload } from '@makemydeal/dr-platform-shared';
import type {
    LockCreditRatingAndTermsByDealType,
    ProtectionItem,
    QualificationId,
    DarwinBaselineProducts
} from '@makemydeal/dr-shared-types';
import type { IPaymentTermsFallback, IShopper, ITradeIn, IVehicle } from '@makemydeal/dr-platform-types';
import type { GatewayMenuResponse, GatewayOfferResponseResult, GatewayTradeResponse } from '@makemydeal/dr-dash-gateway-types';
import type { FeatureTogglesServiceResponseMeta, ValidateDealXgResponse } from '@makemydeal/dr-shared-bff-types';
import type { LeasingTerms, PriorLeaseBalance } from '@makemydeal/dr-offer-redux';
import type {
    CoBuyer,
    CreditScore,
    DcVppVehicleUsage,
    DXGVehicleFulfillment,
    IOffer,
    IPencilOfferInputsDealerFee,
    OfferType,
    ResidualValueType,
    VehicleInclusions,
    VehicleInsurance,
    VehicleReservation,
    VppDrivetrainType,
    VppFuelType
} from '@makemydeal/dr-platform-types';
import type {
    AccessoriesAdapterObject,
    DealProduct,
    SurchargeOverrides,
    Employee,
    DealAccessory
} from '@makemydeal/dr-shared-types';
import type { BaggageEntry } from './dxgTypes';

// interfaces/types
import type { BffInitConfig, Lead, TestDriveBffData, VehicleBffData } from './init';
import type { BffDealer } from './dealer';
import type { VdpSourceState } from './vdpSource';
import type { FinancingTerms, ProcessedTerms, RawTerms, TermData } from './term';
import type { AcceleronData, CalculationDetails, DealLifecycle, DealVehicleProtection, DealXgDetails } from './acceleron';

export type GatewayFinanceTermsPayload = {
    financeTerms: FinancingTerms;
};

export type GatewayLeaseTermsPayload = {
    leaseTerms: LeasingTerms;
    deductDueAtSigning: boolean;
    capCostDetails: {
        adjustedCapCost: number;
        capReductionCash: number;
        grossCapCost: number;
    };
};

export type MergedDeal = {
    mergeSourceDealXgId: string;
    dealRefIdPartner?: {
        partnerCode: string;
        partnerDealerId: string;
    };
    creditApplication?: Record<string, any>;
    previousDealRefId?: string;
};

export type GatewayOfferPayload = {
    applicationName: string;
    bypassDealSummary: boolean;
    cashApplyMethod?: string;
    cmdMessageToShopper: string;
    cmdMessageToSalesperson?: string;
    coBuyer?: CoBuyer;
    commonConsumerId?: string;
    commonOrgId?: string;
    connectionId: string;
    currentCalculationEndpoint?: string;
    creditApp: {
        accelerateCreditAppId: string | undefined;
        coApplicantEmail: string;
        creditAppReferenceId: string;
    };
    creditAppId: string;
    creditScore: CreditScore;
    deal_ref_id: string;
    dealerId: number;
    dealExchangeDealId: string | undefined;
    dealExchangeVersionId: string | undefined;
    dealXgDetails?: {
        isLocked?: boolean;
    };
    enableAppliedLenderInfo?: boolean;
    dealRefId: string;
    dealSummary: [];
    deviceType: string;
    dmvFees?: number;
    downPayment: number | undefined;
    fees?: IPencilOfferInputsDealerFee[];
    financeTerms?: GatewayFinanceTermsPayload;
    balloonTerms?: FinancingTerms;
    fsDealRefId: string;
    globalCustomerId?: string;
    hasBeenSent: boolean | undefined;
    hasShopperEngagedWithOffer: boolean | undefined;
    incentives: Partial<IIncentives & { total: number }>;
    initialOfferPrice: number | undefined;
    isEnabledFniFailFeatureToggle: boolean | undefined;
    isEnabledFniFailure: boolean | undefined;
    isIncentivesCompleted?: boolean;
    isSinglePrice: boolean;
    isTestLead: boolean;
    isTradeCompleted: boolean | undefined;
    isTermsCompleted?: boolean;
    isTradeSkipped: boolean | undefined;
    isUserProgramQuotes?: boolean;
    lead?: Lead;
    leaseTerms?: GatewayLeaseTermsPayload;
    lockCreditRatingAndTermsByDealType?: LockCreditRatingAndTermsByDealType;
    latestPushToDmsPlus?: string;
    messageToDealer: string | undefined;
    metaData: object;
    mergedDeal?: MergedDeal;
    monthlyPayment: number | undefined;
    monthlyPaymentRounded: number | undefined;
    offerId: string | undefined;
    offerPrice: number | undefined;
    offerSource: string;
    offerUpdateType: string | undefined;
    payment: IDealSummary;
    paymentDisclaimer: string;
    paymentTermsFallback: IPaymentTermsFallback | undefined;
    priceDetails?: { priorLeaseBalance?: PriorLeaseBalance };
    sessionId: string;
    shopper: IShopper;
    source: VdpSourceState;
    terms?: RawTerms;
    totalFinanced: number | undefined;
    totalFee: number | undefined;
    totalTax: number | undefined;
    tradeIn: ITradeIn | object;
    transferToDMS?: string;
    type: string;
    vehicle: IVehicle;
    softPullConsentProvided?: boolean;
};

export type SaveOfferPayload = GatewayOfferPayload;

export type SaveOfferProtectionProductsPayload = {
    connectionId: string;
    vehicle: IVehicle;
    offerType: string;
    menuProducts: ProtectionItem[];
};

export type MvDealStatePayload = {
    breakdownState?: 'open' | 'close';
    dealMode?: string;
};

export type AutoSelectStatePayload = {
    checked?: boolean;
    overriddenFields?: string[];
};

export type LeaseResidualOverridePayload = {
    months: number;
    unit: ResidualValueType;
    value: number;
};

export type SaveOfferMenuProductsPayload = SaveOfferDealProductsPayload | SaveOfferProtectionProductsPayload;

export type SaveOfferDealProductsPayload = {
    connectionId: string;
    vehicle: IVehicle;
    offerType: string;
    menuProducts: DealProduct[];
    unselectedProducts: DealProduct[];
    surchargesOverrides: SurchargeOverrides;
};

export interface LeadRoutingOverrideSettings {
    leadRoutingOverrideEnabled: boolean;
    crmOverrideId: string;
    dealertrackOverrideId: number;
}

export interface SaveOfferDealer {
    dealerId: number | undefined;
    dnaAccountId: string | undefined;
    useFnI2: boolean;
    isCrmIntegrationEnabled?: boolean;
    isFordBlueAdvantageEnabled?: boolean;
    isTCPAEnabled?: boolean;
    isTCPACheckboxDisabled?: boolean;
    leadRoutingOverrideSettings?: LeadRoutingOverrideSettings;
    reviewDealUrlEnabled?: boolean;
    listingOverride?: object;
    theme: string | undefined;
    dealerTaxAddress?: unknown;
    enableNonAMDCustomerExperience?: boolean;
}

export type SaveOfferTradePayload = {
    connectionId: string;
    vin?: string;
    tradeData: ITradeIn;
};

export enum BuildPayloadPurpose {
    AppInit = 'APP_INIT',
    DraftSave = 'DRAFT_SAVE',
    MenuInit = 'MENU_INIT',
    OfferSave = 'OFFER_SAVE',
    PushToDms = 'PUSH_TO_DMS',
    PushToDarwin = 'PUSH_TO_DARWIN',
    TradeIn = 'TRADE_IN',
    Unknown = 'UNKNOWN',
    ContinueInDesking = 'CONTINUE_IN_DESKING',
    DeveloperDebugging = 'DEVELOPER_DEBUGGING',
    OpenDeal = 'OPEN_DEAL',
    PrintProposal = 'PRINT_PROPOSAL',
    PublishToSalesView = 'PUBLISH_TO_SALES_VIEW',
    PrintPdfForText = 'PRINT_PDF_FOR_TEXT',
    PrintPdfForEmail = 'PRINT_PDF_FOR_EMAIL',
    SendToDealerTrack = 'SEND_TO_DEALER_TRACK'
}

// saveOffer service payloads
export type ISaveOffer = {
    baggage?: BaggageEntry[];
    /**
     * US2041815: the payment grid for every scenario in this save, already compressed by the browser.
     *
     * The save path stores this as-is and never looks inside it — compression happens client-side so the
     * raw grid (~90 KB per scenario) never travels up the wire. Entries arrive keyed by scope id and are
     * rewritten to deal versions once DXG has assigned them.
     */
    paymentGridBaggage?: PaymentGridBaggagePayload;
    dealValidationNode?: ValidateDealXgResponse;
    accessories?: AccessoriesAdapterObject;
    calculationDetails?: CalculationDetails;
    connectionId?: string;
    dealer?: SaveOfferDealer;
    incentives?: SaveOfferIncentivesPayload;
    menu?: SaveOfferMenuProductsPayload;
    offer?: GatewayOfferPayload;
    qualificationIds?: QualificationId[];
    shopper?: IShopper;
    trade?: SaveOfferTradePayload;
    tradeIns?: ITradeIn[];
    username?: string;
    userFullName?: string;
    proposalId?: string;
    vehicle?: IVehicle;
    vin?: string;
    employees?: Employee[];
    purpose?: BuildPayloadPurpose;
    comparisonInfo?: {
        order: string[];
        alternateScenarios: ISaveOffer[];
    };
    meta?: {
        scenarioId?: string;
        activeDealType?: OfferType;
    };
    vehicleInsurance?: VehicleInsurance;
    dealTemplateId?: string;
    dealLifecycle?: Record<string, string>;
    testDrive?: TestDriveBffData | null;
    vehicleFulfillment?: DXGVehicleFulfillment | null;
    reservation?: VehicleReservation | null;
    mvDealState?: MvDealStatePayload;
    autoSelectState?: AutoSelectStatePayload;
    leaseResidualOverride?: LeaseResidualOverridePayload | null;
    darwinBaseline?: DarwinBaselineProducts;
    softPullConsentProvided?: boolean;
    baggageFuelEconomy?: BaggageFuelEconomyPayload;
    rightToCancelDetails?: { rightToCancelDays?: number; rightToCancelCharge?: number };
};

export type BaggageFuelEconomyVehicleEntry = {
    fuelPricePerGallon?: number;
    milesDriven?: number;
    milesPer?: string;
    isPrimary?: boolean;
    newVehicleMpg?: number;
};

export type BaggageFuelEconomyTradeEntry = {
    fuelPricePerGallon?: number;
    milesDriven?: number;
    milesPer?: string;
    tradeInMpg?: number;
};

export type BaggageFuelEconomyPayload = {
    vehicles?: Record<string, BaggageFuelEconomyVehicleEntry>;
    trades?: Record<string, BaggageFuelEconomyTradeEntry>;
};

export interface IIncentiveItem {
    incentiveName: string;
    incentiveCode: string | number;
    incentiveDescription: string;
    incentiveAmount: number;
    incentiveProgramNumber: string | number;
}

export type IAIOIncentives = IIncentiveItem[];

export type SaveOfferIncentivesPayload = {
    appliedIncentiveAmount: number;
    appliedIncentives: IAIOIncentives;
};

export type ActivityMilestoneActivity = {
    applicationName: string;
    persona: string;
    state: string;
    updatedOn: string;
};
export type ActivityMilestones = {
    creditApplication: ActivityMilestoneActivity;
    documentUpload: ActivityMilestoneActivity;
    incentives: ActivityMilestoneActivity;
    offer: ActivityMilestoneActivity;
    testDrive: ActivityMilestoneActivity;
    tradeIn: ActivityMilestoneActivity;
    vehicleProtectionProducts: ActivityMilestoneActivity;
    vehicleReservation: ActivityMilestoneActivity;
};

export type ActivityMilestonesError = {
    errorMessage: string;
};

export type TradesResponse = {
    tradeIns?: ITradeIn[];
    tradeInSummary?: {
        netTrade?: number;
        totalAcv?: number;
        totalAllowance?: number;
        totalPayoff?: number;
        totalTradeInTaxReduction?: number;
    };
};

export type OfferResponse = {
    accessories: DealAccessory[];
    config: BffInitConfig;
    creditDecision?: object;
    dealer: BffDealer | undefined;
    dealXgDetails?: DealXgDetails;
    errors?: any;
    activityMilestone?: ActivityMilestones | ActivityMilestonesError;
    dealLifecycle?: DealLifecycle;
    offer: IOffer & ProcessedTerms;
    featureToggles: FeatureToggles;
    success: boolean;
    testDrive?: TestDriveBffData;
    trade?: GatewayTradeResponse;
    trades?: TradesResponse;
    terms?: TermData;
    menu?: GatewayMenuResponse;
    vehicle: VehicleBffData;
    traceId?: string;
    meta?: {
        featureToggles: FeatureTogglesServiceResponseMeta;
    };
    dealVehicleProtection: DealVehicleProtection;
    calculationDetails?: CalculationDetails;
    messageToDealer?: string | undefined;
};

export type DxgOfferHistoryOfferResponse = {
    errors: any;
    dealExchangeRecords: AcceleronData[];
};

export type SaveOfferResponse = {
    vin: string;
    connectionId: string;
    snapshotId: string;
    dealXgId: string;
    dealXgVersion: string;
    shopperPageUrl: string;
    copyToDeskingUrl: string;
    creditAppUrl: string;
    testDriveDeepLinkUrl: string;
    docUploadDeepLinkUrl: string;
    fsDealRefId?: string;
    creditAppId?: string;
};
export type SaveDealResponse = {
    dealXgId: string;
    dealXgVersion: string;
    createdDate?: string;
    shopperPageUrl?: string;
    copyToDeskingUrl?: string;
    creditAppUrl?: string;
    testDriveDeepLinkUrl?: string;
    docUploadDeepLinkUrl?: string;
    deferredDownPayment?: { amount: number; paymentDate: string };
};

export type BffOfferResponse = {
    success: boolean;
    // TODO: DASH - we may be able to get this from one of the types above - maybe OfferResponse
    // (if so, we should move whatever we can over to GatewayOfferResponseResult)
    offer: GatewayOfferResponseResult;
};

export interface VehicleInfo extends IVehicle {
    msrp: number;
    condition: string;
    odometerMileage: number;
    invoiceAmount?: number;
    options: any;
    preference: string;
    sellingPrice: number;
    isOverOneTon?: boolean;
    vehicleUse?: DcVppVehicleUsage;
    drivetrainType?: VppDrivetrainType;
    fuelType?: VppFuelType;
    vehicleInclusions?: VehicleInclusions;
    cost?: number;
}
